import { useMemo, useState } from "react";

import i18next from "i18next";

import { normalizePrice } from "../../../utils";

import {
  SeatAisle,
  SeatButton,
  SeatLegend,
  SeatLegendItem,
  SeatMapBlock,
  SeatPassengerTab,
  SeatPassengerTabs,
  SeatRow,
  SeatRowNumber,
  SeatRowTag,
  SeatScroll,
} from "./style";

// цвета ценовых категорий мест: от самой дешёвой к самой дорогой
const TIER_COLORS = ["#2e9e6b", "#1f7ae0", "#8e5bd6", "#e08a1f", "#d6455b"];

/**
 * Схема салона одного рейса. Места приходят от бека вариантами опции: { code: "7A@<рейс>", row, seat, row_layout
 * ("ABC DEF", пробел — проход), price, currency, available, is_exit, passenger_indexes }.
 * Клик по свободному месту закрепляет его за активным пассажиром и переводит на следующего без места;
 * повторный клик по своему месту снимает его. Одно место — одному пассажиру.
 *
 * seatsByPassenger: { [passengerIndex]: code } — выбранные места этого рейса; onPick(passengerIndex, item | null).
 */
const SeatMap = ({ items, passengerIndexes, passengerLabel, seatsByPassenger, onPick, disabled }) => {
  const { t } = i18next;
  const [activePassenger, setActivePassenger] = useState(passengerIndexes[0]);

  // ряды по порядку; раскладка ряда — из первого места ряда
  const rows = useMemo(() => {
    const byRow = new Map();

    items.forEach(item => {
      if (!byRow.has(item.row))
        byRow.set(item.row, { row: item.row, layout: item.row_layout || "", isExit: !!item.is_exit, seats: {} });
      byRow.get(item.row).seats[item.seat] = item;
    });

    return [...byRow.values()].sort((a, b) => a.row - b.row);
  }, [items]);

  // ценовые категории: уникальные цены платных мест по возрастанию
  const tiers = useMemo(() => {
    const prices = [
      ...new Set(items.filter(item => item.available !== false && item.price > 0).map(item => item.price)),
    ].sort((a, b) => a - b);
    const colorOf = {};

    prices.forEach((price, index) => {
      const position = prices.length === 1 ? 0 : Math.round((index / (prices.length - 1)) * (TIER_COLORS.length - 1));
      colorOf[price] = TIER_COLORS[position];
    });

    return { prices, colorOf };
  }, [items]);

  const currency = items[0]?.currency || "";
  const ownerOf = code => passengerIndexes.find(index => seatsByPassenger[index] === code);

  const pick = item => {
    const owner = ownerOf(item.code);

    if (owner !== undefined && owner !== activePassenger) return; // место уже выбрано другому пассажиру заказа

    if (owner === activePassenger) {
      onPick(activePassenger, null);
      return;
    }

    onPick(activePassenger, item);

    // следующий пассажир без места — чтобы семью можно было рассадить подряд
    const next = passengerIndexes.find(index => index !== activePassenger && !seatsByPassenger[index]);
    if (next !== undefined) setActivePassenger(next);
  };

  return (
    <SeatMapBlock>
      <SeatPassengerTabs>
        {passengerIndexes.map(index => (
          <SeatPassengerTab
            key={index}
            type="button"
            $isActive={index === activePassenger}
            onClick={() => setActivePassenger(index)}
          >
            <span className="tab__name">{passengerLabel(index)}</span>
            <span className="tab__seat">
              {seatsByPassenger[index] ? seatsByPassenger[index].split("@")[0] : t("seat_not_selected")}
            </span>
          </SeatPassengerTab>
        ))}
      </SeatPassengerTabs>

      <SeatLegend>
        {tiers.prices.map(price => (
          <SeatLegendItem key={price} $color={tiers.colorOf[price]}>
            {normalizePrice(price)} {currency}
          </SeatLegendItem>
        ))}
        {items.some(item => item.available !== false && !(item.price > 0)) ? (
          <SeatLegendItem $color="#5f6b7a">{t("seat_free_of_charge")}</SeatLegendItem>
        ) : null}
        <SeatLegendItem $color="#d9dde3">{t("seat_occupied")}</SeatLegendItem>
        {rows.some(row => row.isExit) ? <SeatLegendItem $color="#e08a1f">{t("seat_exit_row")}</SeatLegendItem> : null}
      </SeatLegend>

      <SeatScroll>
        {rows.map(row => (
          <SeatRow key={row.row}>
            <SeatRowNumber $isExit={row.isExit} title={row.isExit ? t("seat_exit_row") : undefined}>
              {row.row}
            </SeatRowNumber>

            {row.layout.split("").map((letter, position) => {
              if (letter === " ") return <SeatAisle key={`aisle-${position}`} />;

              const item = row.seats[letter];
              if (!item) return <SeatButton key={letter} type="button" disabled $isEmpty />;

              const owner = ownerOf(item.code);
              const isMine = owner === activePassenger;
              const isTaken = item.available === false || !item.passenger_indexes.includes(activePassenger);

              return (
                <SeatButton
                  key={letter}
                  type="button"
                  title={`${item.code.split("@")[0]} · ${item.price > 0 ? `${normalizePrice(item.price)} ${item.currency}` : t("seat_free_of_charge")}`}
                  $color={item.price > 0 ? tiers.colorOf[item.price] : "#5f6b7a"}
                  $isTaken={isTaken && owner === undefined}
                  $isSelected={owner !== undefined}
                  $isMine={isMine}
                  disabled={disabled || (isTaken && owner === undefined) || (owner !== undefined && !isMine)}
                  onClick={() => pick(item)}
                >
                  {owner !== undefined ? "✓" : letter}
                </SeatButton>
              );
            })}

            {row.isExit ? <SeatRowTag>{t("seat_exit_row")}</SeatRowTag> : null}
          </SeatRow>
        ))}
      </SeatScroll>
    </SeatMapBlock>
  );
};

export default SeatMap;
